import { ColumnDetails, ColumnType } from '../../../../../../../../grid/types/common';
import { translateFn } from '../../../../../../../../utils/translator.helper';
import { html, TemplateResult } from 'lit';
import { AntimalwareOnDemandScanTask, RecurrenceType } from '@nirvana/shared/dtos/types/policies/common';
import {
  getRepeatIntervalFormOptions,
  getWeekDaysFormOptions,
  WeekDaysValues,
  WeekDayValueToScheduleFieldName,
} from '../../../../../../../common/policyPanel/uiSettings/schedulerTemplate/types';
import { GridFilterUiConfiguration } from '../../../../../../../../types/filters/types';
import { getAntimalwareOnDemandScanTaskScanTypeGridOptions } from '../../../../../utils/uiSettings/antimalware/antimalware.values';

export enum Column {
  NAME = 1,
  SCAN_TYPE = 2,
  REPEAT_INTERVAL = 3,
  FIRST_RUN = 4,
}

export enum ColumnKeys {
  NAME = 'taskName',
  SCAN_TYPE = 'scanType',
  REPEAT_INTERVAL = 'scheduler',
  FIRST_RUN = 'startDate',
}

export const GRID_COLUMNS = [Column.NAME, Column.SCAN_TYPE, Column.REPEAT_INTERVAL, Column.FIRST_RUN];

export const FILTERS = [
  { [ColumnKeys.NAME]: { contains: '' } },
  { [ColumnKeys.SCAN_TYPE]: { in: [] } },
  {
    [ColumnKeys.FIRST_RUN]: {
      timeRange: {},
    },
  },
];
export const PREDEFINED_FILTERS_KEYS = [ColumnKeys.NAME, ColumnKeys.SCAN_TYPE, ColumnKeys.FIRST_RUN];
export function filtersUiConfigs(): Array<GridFilterUiConfiguration> {
  return [
    {
      key: ColumnKeys.SCAN_TYPE,
      config: {
        allowSelectNone: true,
      },
    },
    {
      key: ColumnKeys.FIRST_RUN,
      config: {
        activeRange: 365,
        max: null,
        enableSelectAllToDate: true,
        additionalTimeRangeOptions: [
          {
            label: translateFn('Last 90 days'),
            value: 90,
          },
          {
            label: translateFn('Last 180 days'),
            value: 180,
          },
          {
            label: translateFn('Last year'),
            value: 365,
          },
        ],
      },
    },
  ];
}

export function getGridColumnDetails(): Array<ColumnDetails<Column, ColumnKeys>> {
  return [
    {
      column: Column.NAME,
      key: ColumnKeys.NAME,
      columnName: translateFn('Task name'),
      type: ColumnType.STRING,
      removable: false,
      sortable: true,
      editingDisabled: true,
    },
    {
      column: Column.SCAN_TYPE,
      key: ColumnKeys.SCAN_TYPE,
      columnName: translateFn('Scan type'),
      values: getAntimalwareOnDemandScanTaskScanTypeGridOptions(),
      type: ColumnType.ENUM,
      removable: false,
      sortable: true,
      editingDisabled: true,
    },
    {
      column: Column.REPEAT_INTERVAL,
      key: ColumnKeys.REPEAT_INTERVAL,
      columnName: translateFn('Repeat interval'),
      type: ColumnType.STRING,
      removable: false,
      sortable: false,
      editingDisabled: true,
      filteringDisabled: true,
    },
    {
      column: Column.FIRST_RUN,
      key: ColumnKeys.FIRST_RUN,
      columnName: translateFn('First run'),
      type: ColumnType.DATE,
      removable: false,
      sortable: false,
      filterResetValue: {
        timeRange: {},
      },
    },
  ];
}

export function nameCellCustomRenderer(
  dispatchClickEventFn: (item) => void,
  rowData: unknown & { item: AntimalwareOnDemandScanTask }
): TemplateResult {
  return html` <span
    class="accent cell-label"
    style="cursor: pointer"
    @click="${() => dispatchClickEventFn(rowData.item)}"
    >${rowData.item.taskName}</span
  >`;
}

export function repeatIntervalCellCustomRenderer(
  rowData: unknown & { item: AntimalwareOnDemandScanTask }
): TemplateResult {
  let cellText = '';

  switch (rowData.item.scheduler.recurrenceType) {
    case RecurrenceType.SCHEDULE:
      cellText =
        cellText +
        translateFn('Every') +
        ' ' +
        rowData.item.scheduler.runEvery +
        ' ' +
        getRepeatIntervalFormOptions()[rowData.item.scheduler.runPer].title;
      break;

    case RecurrenceType.WEEKDAYS:
      const weekDays = WeekDaysValues.filter(x => rowData.item.scheduler[WeekDayValueToScheduleFieldName[x]]);
      const weekDaysFormOptions = getWeekDaysFormOptions();

      // get the labels for the selected week days
      const days: string[] = [];
      weekDaysFormOptions.forEach(option => {
        if (weekDays.includes(option.value)) {
          days.push(option.label);
        }
      });

      cellText = cellText + translateFn('Every') + ' ' + days.join(', ');
      break;
  }

  return html`<span class="cell-label">${cellText}</span>`;
}

export function firstRunCellCustomRenderer(rowData: unknown & { item: AntimalwareOnDemandScanTask }): TemplateResult {
  return html`<span class="cell-label"
    >${rowData.item.scheduler.startDate.date +
    ' ' +
    rowData.item.scheduler.startDate.hours.toString().padStart(2, '0') +
    ':' +
    rowData.item.scheduler.startDate.minutes.toString().padStart(2, '0')}</span
  >`;
}
